import { CheckCircle2, XCircle, ArrowRight, Trash2, RotateCcw, Lightbulb, Sparkles, AlertTriangle } from "lucide-react";
import AtsGauge from "../app/AtsGauge";
import CvDownloadPanel from "../app/CvDownloadPanel";

const barColor = (s) => (s >= 75 ? "bg-brand" : s >= 50 ? "bg-warning" : "bg-danger");

const H3 = ({ children }) => <h3 className="font-display text-lg font-bold text-ink mb-3">{children}</h3>;

function Bars({ items, prefix }) {
  return (
    <div className="space-y-4">
      {(items || []).map((c) => (
        <div key={c.key} data-testid={`${prefix}-${c.key}`}>
          <div className="flex items-baseline justify-between gap-3 text-[15px] mb-1.5">
            <span className="text-ink">{c.label}</span>
            <span className="font-semibold text-ink tabular-nums">{c.score}%</span>
          </div>
          <div className="h-2 rounded-full bg-subtle overflow-hidden"><div className={`h-full rounded-full ${barColor(c.score)} transition-[width] duration-700`} style={{ width: `${c.score}%` }} /></div>
          {c.reason && <p className="text-sm text-body mt-1.5 leading-relaxed">{c.reason}</p>}
        </div>
      ))}
    </div>
  );
}

function Checks({ checks }) {
  return (
    <ul className="space-y-2.5" data-testid="free-check-ats-list">
      {(checks || []).map((c) => (
        <li key={c.id} className="flex gap-2.5">
          {c.ok ? <CheckCircle2 className="w-5 h-5 text-success shrink-0 mt-px" aria-label="Passed" /> : <XCircle className="w-5 h-5 text-danger shrink-0 mt-px" aria-label="Needs work" />}
          <div className="min-w-0">
            <div className="text-[15px] text-ink">{c.label}</div>
            {!c.ok && c.tip && <p className="text-sm text-body">{c.tip}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

const Chips = ({ items, cls }) => (
  <div className="flex flex-wrap gap-1.5">{items.map((k) => <span key={k} className={`text-sm px-2.5 py-1 rounded-full border ${cls}`}>{k}</span>)}</div>
);

function Match({ m }) {
  const unmet = (m.mandatory || []).filter((x) => !x.met);
  return (
    <div className="mt-8 pt-8 border-t border-line" data-testid="free-check-match">
      <H3>How well you match the job</H3>
      {m.capped && (
        <p className="mb-4 rounded-xl bg-warning-soft border border-warning/25 p-3.5 text-sm text-ink flex gap-2">
          <AlertTriangle className="w-4 h-4 text-warning mt-0.5 shrink-0" /> Your match is capped at {m.cap}% because the job lists a must-have your CV doesn't show.
        </p>
      )}
      <div className="grid lg:grid-cols-2 gap-8">
        <Bars items={m.categories} prefix="free-check-match-cat" />
        <div className="space-y-5">
          {unmet.length > 0 && (
            <div><div className="text-sm font-semibold text-ink mb-2">Must-haves not shown on your CV</div><Chips items={unmet.map((x) => x.requirement)} cls="border-danger/30 bg-danger-soft text-ink" /></div>
          )}
          {m.matched?.length > 0 && <div><div className="text-sm font-semibold text-ink mb-2">Your CV already shows</div><Chips items={m.matched} cls="border-brand/30 bg-brand-soft text-brand-text" /></div>}
          {m.missing?.length > 0 && <div><div className="text-sm font-semibold text-ink mb-2">The job asks for, but your CV doesn't mention</div><Chips items={m.missing} cls="border-line-strong/60 text-body" /></div>}
        </div>
      </div>
    </div>
  );
}

export default function FreeCheckResult({ result, token, signedIn, onSave, onReset, onDelete }) {
  const h = result.health || {};
  const m = result.match;
  const gen = result.generated_cv;
  return (
    <section id="free-check-result" data-testid="free-check-result" className="relative max-w-7xl mx-auto px-5 sm:px-6 pb-20 scroll-mt-20 af-fade-up">
      {gen && (
        <div className="gi-card p-5 sm:p-8 mb-6" data-testid="free-check-generated-cv">
          <div className="gi-eyebrow">Built from your LinkedIn profile</div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-ink mt-1.5">Your new CV is ready</h2>
          <p className="text-[15px] text-body mt-1.5 mb-5 max-w-2xl">We only used facts from your profile. Check every line is accurate before you send it, then pick a design and download.</p>
          <CvDownloadPanel allowPhoto={false} cv={gen} testid="free-check-cv-download" pathFor={(fmt, d) => `/guest/cv-check/${token}/cv.${fmt}?template=${d}`} />
        </div>
      )}
      <div className="gi-card p-5 sm:p-8">
        <div className="flex flex-col md:flex-row gap-6 md:items-center">
          <div className="flex gap-4 justify-center md:justify-start shrink-0">
            <div data-testid="free-check-health-score"><AtsGauge score={h.overall ?? 0} size={136} label="CV HEALTH" /></div>
            {m && <div data-testid="free-check-match-score"><AtsGauge score={m.overall ?? 0} size={136} label="JOB MATCH" /></div>}
          </div>
          <div className="min-w-0">
            <div className="gi-eyebrow">{gen ? "Your new CV, checked" : "Your free CV check"}</div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-ink mt-1.5" data-testid="free-check-headline">{h.headline || "Here's how your CV reads today."}</h2>
            <p className="text-sm text-faint mt-2 break-words">{result.filename} · An honest estimate, not a guarantee.</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          <div><H3>What makes up your score</H3><Bars items={h.categories} prefix="free-check-cat" /></div>
          <div><H3>ATS readability checks</H3><Checks checks={h.checks} /></div>
        </div>

        {h.improvements?.length > 0 && (
          <div className="mt-8 pt-8 border-t border-line">
            <H3>Your top fixes</H3>
            <ol className="grid md:grid-cols-2 gap-3" data-testid="free-check-improvements">
              {h.improvements.map((x, i) => (
                <li key={i} className="rounded-xl bg-canvas border border-line p-4">
                  <div className="text-[15px] font-semibold text-ink flex gap-2"><Lightbulb className="w-4 h-4 text-warning mt-0.5 shrink-0" />{x.issue}</div>
                  {x.fix && <p className="text-[15px] text-body mt-1.5 leading-relaxed">{x.fix}</p>}
                </li>
              ))}
            </ol>
          </div>
        )}

        {h.strengths?.length > 0 && (
          <div className="mt-8">
            <H3>What's already working</H3>
            <ul className="space-y-2" data-testid="free-check-strengths">
              {h.strengths.map((s) => <li key={s} className="flex gap-2.5 text-[15px] text-body"><Sparkles className="w-4 h-4 text-kesh mt-1 shrink-0" />{s}</li>)}
            </ul>
          </div>
        )}

        {m && <Match m={m} />}

        <div className="mt-8 rounded-2xl bg-navy text-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="free-check-cta">
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-bold">{gen ? "Save this CV and tailor it to a job, free" : "Fix these and tailor your CV to a job, free"}</div>
            <p className="text-white/80 text-[15px] mt-1">{signedIn ? "We'll use this CV in CV Studio" : "Create a free account and we'll bring this CV across"}{m ? " with the job ad" : ""}, so you won't need to upload it again.</p>
          </div>
          <button data-testid="free-check-save" onClick={onSave} className="gi-btn gi-btn-primary gi-btn-lg shrink-0">
            {signedIn ? "Continue in CV Studio" : "Save my CV & continue free"} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 sm:justify-end">
          <span className="text-sm text-faint mr-auto">Kept until you delete it.</span>
          <button data-testid="free-check-again" onClick={onReset} className="gi-btn gi-btn-secondary gi-btn-sm"><RotateCcw className="w-4 h-4" /> Check another CV</button>
          <button data-testid="free-check-delete" onClick={onDelete} className="gi-btn gi-btn-sm text-danger hover:bg-danger-soft"><Trash2 className="w-4 h-4" /> Delete my CV now</button>
        </div>
      </div>
    </section>
  );
}
